import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { useCreateIncident } from '@/hooks/useAdminCommunications';
import { incidentSchema, type IncidentFormData } from '@/schemas/communication.schema';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';

interface IncidentFormProps {
  onSuccess?: () => void;
}

export function IncidentForm({ onSuccess }: IncidentFormProps) {
  const create = useCreateIncident();
  const { register, handleSubmit, formState: { errors } } = useForm<IncidentFormData>({
    resolver: zodResolver(incidentSchema),
    defaultValues: { severity: 'minor', status: 'investigating', affectedServices: [] },
  });

  const onSubmit = (data: IncidentFormData) => {
    create.mutate(data, { onSuccess: () => onSuccess?.() });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <Input label="Titre" error={errors.title?.message} {...register('title')} />
      <div className="grid grid-cols-2 gap-4">
        <Select label="Sévérité" options={[
          { value: 'critical', label: 'Critique' },
          { value: 'major', label: 'Majeur' },
          { value: 'minor', label: 'Mineur' },
          { value: 'info', label: 'Info' },
        ]} error={errors.severity?.message} {...register('severity')} />
        <Select label="Statut" options={[
          { value: 'investigating', label: 'Investigation' },
          { value: 'identified', label: 'Identifié' },
          { value: 'monitoring', label: 'Surveillance' },
          { value: 'resolved', label: 'Résolu' },
        ]} error={errors.status?.message} {...register('status')} />
      </div>
      <div>
        <label className="mb-1.5 block text-sm font-medium text-neutral-700 dark:text-neutral-300">Description</label>
        <textarea
          className="w-full rounded-lg border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-800 px-3 py-2 text-sm text-neutral-900 dark:text-neutral-100 placeholder:text-neutral-400 dark:placeholder:text-neutral-500 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 min-h-[80px]"
          {...register('description')}
        />
        {errors.description && <p className="mt-1 text-xs text-danger-500">{errors.description.message}</p>}
      </div>
      <Button type="submit" loading={create.isPending}>Créer l'incident</Button>
    </form>
  );
}
